import { useEffect, useRef } from 'react';
import useAuthStore from '../store/authStore';

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

export const useSessionTimeout = () => {
  const { logout, user, isAdmin } = useAuthStore();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset timer on user activity
  const resetTimer = () => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }

    // Only set timers for admin users
    if (user && isAdmin) {
      // Set warning timer
      warningRef.current = setTimeout(() => {
        showWarning();
      }, SESSION_TIMEOUT - WARNING_TIME);

      // Set logout timer
      timeoutRef.current = setTimeout(() => {
        handleLogout();
      }, SESSION_TIMEOUT);
    }
  };

  // Show warning modal
  const showWarning = () => {
    const warningModal = document.createElement('div');
    warningModal.id = 'session-warning-modal';
    warningModal.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.8);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          background: white;
          padding: 30px;
          border-radius: 12px;
          max-width: 400px;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        ">
          <h3 style="color: #dc2626; margin-bottom: 15px; font-size: 20px;">Session Expiring Soon</h3>
          <p style="color: #374151; margin-bottom: 20px; line-height: 1.5;">
            Your admin session will expire in 5 minutes due to inactivity.
          </p>
          <div style="display: flex; gap: 10px; justify-content: center;">
            <button id="extend-session" style="
              background: #3b82f6;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 600;
            ">Extend Session</button>
            <button id="logout-now" style="
              background: #dc2626;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 600;
            ">Logout Now</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(warningModal);

    // Add event listeners
    document.getElementById('extend-session').addEventListener('click', () => {
      document.body.removeChild(warningModal);
      resetTimer();
    });

    document.getElementById('logout-now').addEventListener('click', () => {
      document.body.removeChild(warningModal);
      handleLogout();
    });
  };

  // Handle logout
  const handleLogout = async () => {
    console.log('[SESSION TIMEOUT] Auto-logout due to inactivity');
    
    // Send audit log
    try {
      await fetch('/api/auth/log-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'session_timeout',
          details: 'Auto-logout due to inactivity',
          timestamp: new Date().toISOString()
        }),
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to log session timeout:', error);
    }

    await logout();
  };

  // Track user activity
  useEffect(() => {
    if (!user || !isAdmin) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial timer setup
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
    };
  }, [user, isAdmin]);

  return { resetTimer };
}; 